import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Button,
  Chip,
  makeStyles,
} from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import LaunchIcon from '@material-ui/icons/Launch';
import ReplayIcon from '@material-ui/icons/Replay';
import UndoIcon from '@material-ui/icons/Undo';
import Alert from '@material-ui/lab/Alert';
import {
  useApi,
  configApiRef,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { Env, ServiceEntry } from '../promoteUtils';
import { DeployRun, useDeployRuns } from '../useDeployRuns';
import { VersionMap } from './StepSelect';

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  statusDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
  },
  serviceRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    background:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(0,0,0,0.02)',
  },
  serviceRowActive: {
    borderColor: theme.palette.primary.main,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(139, 92, 246, 0.08)'
        : 'rgba(139, 92, 246, 0.04)',
  },
  serviceRowFailed: {
    borderColor: theme.palette.error.main,
  },
  serviceRowDone: {
    borderColor: theme.palette.success.main,
  },
  serviceRowHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  idxBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    display: 'grid',
    placeItems: 'center',
    fontFamily: 'monospace',
    fontSize: 11,
    fontWeight: 600,
    border: `1.5px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
  },
  idxBadgeActive: {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
  },
  idxBadgeDone: {
    borderColor: theme.palette.success.main,
    background: theme.palette.success.main,
    color: '#fff',
  },
  idxBadgeFailed: {
    borderColor: theme.palette.error.main,
    background: theme.palette.error.main,
    color: '#fff',
  },
  serviceName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: 13,
    flex: 1,
  },
  versionArrow: {
    fontFamily: 'monospace',
    fontSize: 12,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  logsPaper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 360,
    maxHeight: 'calc(100vh - 400px)',
  },
  logsHeader: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  logsBody: {
    flex: 1,
    overflow: 'auto',
    padding: theme.spacing(1, 2),
    fontFamily: 'monospace',
    fontSize: 11,
    lineHeight: 1.6,
  },
  logLine: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  logTime: {
    color: theme.palette.text.hint,
    flexShrink: 0,
  },
  logLevel: {
    flexShrink: 0,
    minWidth: 42,
  },
  errPanel: {
    marginTop: theme.spacing(2),
  },
}));

type PhaseKind =
  | 'pending'
  | 'queued'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'rolled-back';

interface ServiceState {
  phase: PhaseKind;
  run?: DeployRun | null;
  error?: string;
  dispatchedAt?: number;
}

interface LogLine {
  t: string;
  lvl: 'INFO' | 'OK' | 'ERR';
  msg: string;
  tone: 'info' | 'ok' | 'err';
  url?: string;
}

export interface StepRolloutProps {
  orderedServices: ServiceEntry[];
  versions: VersionMap;
  sourceEnv: Env;
  targetEnv: Env;
  hasNotes: boolean;
  bundleVersion: string;
  onDone: (result: { success: boolean }) => void;
}

export const StepRollout = ({
  orderedServices,
  versions,
  sourceEnv,
  targetEnv,
  hasNotes,
  bundleVersion,
  onDone,
}: StepRolloutProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const { waitForRun } = useDeployRuns();

  const [overall, setOverall] = useState<'running' | 'halted' | 'done' | 'rolling-back' | 'rollback-done' | 'rollback-failed'>(
    'running',
  );
  const [perService, setPerService] = useState<Record<string, ServiceState>>(() => {
    const init: Record<string, ServiceState> = {};
    for (const s of orderedServices) init[s.name] = { phase: 'pending' };
    return init;
  });
  const [logs, setLogs] = useState<LogLine[]>([]);
  const [currentReleaseSet, setCurrentReleaseSet] = useState<'idle' | 'setting' | 'done' | 'error'>('idle');
  const [currentReleaseError, setCurrentReleaseError] = useState<string | null>(null);

  // Rollback snapshot — captured once at mount
  const rollbackVersions = useRef<Record<string, string | undefined>>({});
  useEffect(() => {
    for (const s of orderedServices) {
      rollbackVersions.current[s.name] = versions[s.name]?.[targetEnv];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancellation
  const abortRef = useRef<AbortController | null>(null);
  const startedRef = useRef(false);

  const logsRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (logsRef.current) logsRef.current.scrollTop = logsRef.current.scrollHeight;
  }, [logs.length]);

  const addLog = useCallback(
    (lvl: LogLine['lvl'], msg: string, tone: LogLine['tone'], url?: string) => {
      const t = new Date().toISOString().slice(11, 19);
      setLogs((prev) => [...prev, { t, lvl, msg, tone, url }]);
    },
    [],
  );

  const setSvc = useCallback((name: string, patch: Partial<ServiceState>) => {
    setPerService((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));
  }, []);

  const dispatchDeploy = useCallback(
    async (service: string, tag: string) => {
      // Probed versions come from the app's /version endpoint (e.g. "0.18.31"),
      // but ECR tags and deploy.yml inputs are prefixed with "v" (e.g. "v0.18.31").
      const deployTag = tag.startsWith('v') ? tag : `v${tag}`;
      const baseUrl = await discoveryApi.getBaseUrl('release-manager');
      const res = await backstageFetch(`${baseUrl}/deploy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ service, environment: targetEnv, tag: deployTag }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `dispatch failed (${res.status})`);
      }
    },
    [backstageFetch, discoveryApi, targetEnv],
  );

  const runOne = useCallback(
    async (svc: ServiceEntry, tag: string, signal: AbortSignal): Promise<boolean> => {
      addLog('INFO', `[${svc.name}] dispatching deploy of ${tag} → ${targetEnv}`, 'info');
      const dispatchedAt = Date.now();
      setSvc(svc.name, { phase: 'queued', dispatchedAt, error: undefined, run: null });
      try {
        await dispatchDeploy(svc.name, tag);
      } catch (err: any) {
        addLog('ERR', `[${svc.name}] dispatch failed: ${err.message}`, 'err');
        setSvc(svc.name, { phase: 'failed', error: err.message });
        return false;
      }
      addLog('INFO', `[${svc.name}] dispatched · waiting for GitHub run to appear`, 'info');

      const seenPhases = new Set<string>();
      let seenRunId: number | null = null;
      let lastErrorLoggedAt = 0;
      const result = await waitForRun({
        service: svc.name,
        environment: targetEnv,
        dispatchedAt,
        signal,
        onTick: ({ polls, totalRuns, error }) => {
          if (error) {
            // Don't spam — log once per 30s
            if (Date.now() - lastErrorLoggedAt > 30_000) {
              addLog('ERR', `[${svc.name}] deploy-runs poll error: ${error}`, 'err');
              lastErrorLoggedAt = Date.now();
            }
            return;
          }
          // If we've polled several times without finding a match, log a heartbeat
          if (seenRunId === null && polls > 0 && polls % 6 === 0) {
            addLog(
              'INFO',
              `[${svc.name}] still waiting for run · ${polls * 5}s elapsed · ${totalRuns} runs in recent list`,
              'info',
            );
          }
        },
        onUpdate: (run) => {
          if (!run) return;
          if (run.id !== seenRunId) {
            seenRunId = run.id;
            addLog('INFO', `[${svc.name}] matched GitHub run ${run.id} (${run.status})`, 'info', run.url);
          }
          const key = `${run.id}:${run.status}`;
          if (!seenPhases.has(key)) {
            seenPhases.add(key);
            if (run.status === 'in_progress') {
              addLog('INFO', `[${svc.name}] run in progress`, 'info');
            }
          }
          if (run.status === 'in_progress') {
            setSvc(svc.name, { phase: 'in_progress', run });
          } else if (run.status === 'queued') {
            setSvc(svc.name, { phase: 'queued', run });
          }
        },
      });

      if (result.success && result.run) {
        addLog('OK', `[${svc.name}] deployed ${tag} → ${targetEnv}`, 'ok', result.run.url);
        setSvc(svc.name, { phase: 'done', run: result.run });
        return true;
      }

      const runUrl = result.run?.url;
      addLog('ERR', `[${svc.name}] ${result.error ?? result.reason ?? 'failed'}`, 'err', runUrl);
      setSvc(svc.name, {
        phase: 'failed',
        error: result.error ?? result.reason ?? 'failed',
        run: result.run ?? null,
      });
      return false;
    },
    [addLog, dispatchDeploy, setSvc, targetEnv, waitForRun],
  );

  const setCurrentReleaseMutation = useCallback(async () => {
    if (!hasNotes || !bundleVersion) return;
    setCurrentReleaseSet('setting');
    setCurrentReleaseError(null);
    try {
      const graphqlUrl = `${config.getString('backend.baseUrl')}/api/proxy/release-notes/graphql`;
      const res = await backstageFetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation SetCurrentRelease($version: String!) {
            setCurrentRelease(version: $version) { version }
          }`,
          variables: { version: bundleVersion },
        }),
      });
      const data = await res.json();
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }
      setCurrentReleaseSet('done');
      addLog('OK', `release-notes: set current to ${bundleVersion}`, 'ok');
    } catch (err: any) {
      setCurrentReleaseSet('error');
      setCurrentReleaseError(err.message || 'failed');
      addLog('ERR', `release-notes: setCurrentRelease failed: ${err.message}`, 'err');
    }
  }, [addLog, backstageFetch, bundleVersion, config, hasNotes]);

  const runPipelineFrom = useCallback(
    async (startIdx: number) => {
      abortRef.current = new AbortController();
      const signal = abortRef.current.signal;
      setOverall('running');

      for (let i = startIdx; i < orderedServices.length; i++) {
        if (signal.aborted) return;
        const svc = orderedServices[i];
        const tag = versions[svc.name]?.[sourceEnv];
        if (!tag) {
          addLog('ERR', `[${svc.name}] missing source version`, 'err');
          setSvc(svc.name, { phase: 'failed', error: 'missing source version' });
          setOverall('halted');
          return;
        }
        const ok = await runOne(svc, tag, signal);
        if (!ok) {
          setOverall('halted');
          return;
        }
      }

      setOverall('done');
      addLog('OK', 'All services deployed successfully', 'ok');
      await setCurrentReleaseMutation();
      onDone({ success: true });
    },
    [
      addLog,
      onDone,
      orderedServices,
      runOne,
      setCurrentReleaseMutation,
      setSvc,
      sourceEnv,
      versions,
    ],
  );

  // Auto-start on mount
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    addLog('INFO', `Starting rollout to ${targetEnv}`, 'info');
    runPipelineFrom(0);
    return () => {
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRetry = useCallback(() => {
    const failedIdx = orderedServices.findIndex(
      (s) => perService[s.name]?.phase === 'failed',
    );
    if (failedIdx === -1) return;
    // Reset failed + any pending after it
    const next = { ...perService };
    for (let i = failedIdx; i < orderedServices.length; i++) {
      const n = orderedServices[i].name;
      if (next[n]?.phase === 'failed' || next[n]?.phase === 'pending') {
        next[n] = { phase: 'pending' };
      }
    }
    setPerService(next);
    runPipelineFrom(failedIdx);
  }, [orderedServices, perService, runPipelineFrom]);

  const onRollbackAll = useCallback(async () => {
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    setOverall('rolling-back');
    addLog('INFO', 'Starting rollback for completed services (reverse dep order)', 'info');

    const doneServices = orderedServices
      .map((s, i) => ({ s, i }))
      .filter(({ s }) => perService[s.name]?.phase === 'done')
      .reverse();

    for (const { s } of doneServices) {
      if (signal.aborted) return;
      const rollbackTag = rollbackVersions.current[s.name];
      if (!rollbackTag) {
        addLog('ERR', `[${s.name}] no rollback version captured`, 'err');
        setOverall('rollback-failed');
        return;
      }
      addLog('INFO', `[${s.name}] rolling back to ${rollbackTag}`, 'info');
      const dispatchedAt = Date.now();
      setSvc(s.name, { phase: 'queued', dispatchedAt });
      try {
        await dispatchDeploy(s.name, rollbackTag);
      } catch (err: any) {
        addLog('ERR', `[${s.name}] rollback dispatch failed: ${err.message}`, 'err');
        setSvc(s.name, { phase: 'failed', error: err.message });
        setOverall('rollback-failed');
        return;
      }
      const result = await waitForRun({
        service: s.name,
        environment: targetEnv,
        dispatchedAt,
        signal,
      });
      if (!result.success) {
        addLog('ERR', `[${s.name}] rollback failed: ${result.error ?? result.reason}`, 'err', result.run?.url);
        setSvc(s.name, { phase: 'failed', error: result.error ?? 'rollback failed', run: result.run ?? null });
        setOverall('rollback-failed');
        return;
      }
      addLog('OK', `[${s.name}] rolled back to ${rollbackTag}`, 'ok', result.run?.url);
      setSvc(s.name, { phase: 'rolled-back', run: result.run });
    }
    setOverall('rollback-done');
    addLog('OK', 'Rollback complete', 'ok');
  }, [
    addLog,
    dispatchDeploy,
    orderedServices,
    perService,
    setSvc,
    targetEnv,
    waitForRun,
  ]);

  const overallDot = {
    running: '#3b5bdb',
    halted: '#e03131',
    done: '#2f9e44',
    'rolling-back': '#f08c00',
    'rollback-done': '#868e96',
    'rollback-failed': '#e03131',
  }[overall];

  const overallLabel = {
    running: `Rolling out to ${targetEnv}…`,
    halted: 'Rollout halted',
    done: 'Rollout complete',
    'rolling-back': 'Rolling back…',
    'rollback-done': 'Rollback complete',
    'rollback-failed': 'Rollback failed',
  }[overall];

  const failedService = orderedServices.find(
    (s) => perService[s.name]?.phase === 'failed',
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <Paper variant="outlined">
          <div className={classes.header}>
            <div className={classes.statusDot} style={{ background: overallDot }} />
            <Box flex={1}>
              <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                {overallLabel}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Sequential · {orderedServices.length} services
              </Typography>
            </Box>
            {overall === 'halted' && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ReplayIcon />}
                  onClick={onRetry}
                >
                  Retry service
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<UndoIcon />}
                  onClick={onRollbackAll}
                >
                  Rollback all
                </Button>
              </>
            )}
          </div>
          <Box px={2} pb={2}>
            {orderedServices.map((svc, i) => {
              const st = perService[svc.name] ?? { phase: 'pending' };
              const sourceV = versions[svc.name]?.[sourceEnv];
              const targetV = versions[svc.name]?.[targetEnv];
              const isActive = st.phase === 'queued' || st.phase === 'in_progress';
              const isDone = st.phase === 'done' || st.phase === 'rolled-back';
              const isFailed = st.phase === 'failed';
              const rowClass = `${classes.serviceRow} ${
                isActive ? classes.serviceRowActive : ''
              } ${isFailed ? classes.serviceRowFailed : ''} ${isDone ? classes.serviceRowDone : ''}`;
              const badgeClass = `${classes.idxBadge} ${
                isActive ? classes.idxBadgeActive : ''
              } ${isDone ? classes.idxBadgeDone : ''} ${isFailed ? classes.idxBadgeFailed : ''}`;
              return (
                <div key={svc.name} className={rowClass}>
                  <div className={classes.serviceRowHeader}>
                    <div className={badgeClass}>
                      {isDone ? <CheckIcon style={{ fontSize: 14 }} /> :
                       isFailed ? <CloseIcon style={{ fontSize: 14 }} /> :
                       i + 1}
                    </div>
                    <div className={classes.serviceName}>{svc.name}</div>
                    <div className={classes.versionArrow}>
                      <span>{targetV ?? '—'}</span>
                      <ArrowForwardIcon style={{ fontSize: 12 }} />
                      <span style={{ fontWeight: 600 }}>{sourceV ?? '—'}</span>
                    </div>
                    <Chip
                      size="small"
                      label={phaseLabel(st.phase)}
                      color={
                        isFailed ? 'secondary' : isActive ? 'primary' : 'default'
                      }
                      variant={isDone || isFailed || isActive ? 'default' : 'outlined'}
                    />
                    {st.run?.url && (
                      <Button
                        size="small"
                        href={st.run.url}
                        target="_blank"
                        startIcon={<LaunchIcon style={{ fontSize: 12 }} />}
                      >
                        Run
                      </Button>
                    )}
                  </div>
                  {isActive && (
                    <Box mt={1} pl={4}>
                      <LinearProgress />
                    </Box>
                  )}
                  {isFailed && st.error && (
                    <Box mt={1} pl={4}>
                      <Typography
                        variant="caption"
                        color="error"
                        style={{ fontFamily: 'monospace' }}
                      >
                        {st.error}
                      </Typography>
                    </Box>
                  )}
                </div>
              );
            })}
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper variant="outlined" className={classes.logsPaper}>
          <div className={classes.logsHeader}>
            <Typography
              variant="overline"
              color="textSecondary"
              style={{ letterSpacing: 0.8, flex: 1 }}
            >
              Live logs
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
              deploy.yml
            </Typography>
          </div>
          <div className={classes.logsBody} ref={logsRef}>
            {logs.map((l, i) => (
              <div key={i} className={classes.logLine}>
                <span className={classes.logTime}>{l.t}</span>
                <span
                  className={classes.logLevel}
                  style={{ color: logColor(l.tone) }}
                >
                  {l.lvl}
                </span>
                <span style={{ color: l.tone === 'err' ? '#e03131' : undefined, flex: 1 }}>
                  {l.msg}
                  {l.url && (
                    <>
                      {' '}
                      <a href={l.url} target="_blank" rel="noreferrer">
                        (run)
                      </a>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </Paper>

        {overall === 'halted' && failedService && (
          <Box mt={2} className={classes.errPanel}>
            <Alert severity="error">
              <strong>Pipeline halted.</strong> {failedService.name} failed: {perService[failedService.name]?.error}. Remaining services queued. Use <em>Retry service</em> to re-dispatch, or <em>Rollback all</em> to revert completed services.
            </Alert>
          </Box>
        )}
        {overall === 'rolling-back' && (
          <Box mt={2}>
            <Alert severity="warning">Rolling back completed services…</Alert>
          </Box>
        )}
        {overall === 'rollback-done' && (
          <Box mt={2}>
            <Alert severity="info">Rollback complete. Pipeline exited.</Alert>
          </Box>
        )}
        {overall === 'rollback-failed' && (
          <Box mt={2}>
            <Alert severity="error">Rollback failed partway through — manual intervention required.</Alert>
          </Box>
        )}
        {overall === 'done' && currentReleaseSet === 'error' && (
          <Box mt={2}>
            <Alert severity="warning">
              Rollout succeeded but setCurrentRelease failed: {currentReleaseError}. Set it manually from the Versions page.
            </Alert>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

function phaseLabel(p: PhaseKind): string {
  switch (p) {
    case 'pending': return 'Queued';
    case 'queued': return 'Queued';
    case 'in_progress': return 'Deploying';
    case 'done': return 'Deployed';
    case 'failed': return 'Failed';
    case 'rolled-back': return 'Rolled back';
  }
}

function logColor(tone: 'info' | 'ok' | 'err'): string {
  if (tone === 'err') return '#e03131';
  if (tone === 'ok') return '#2f9e44';
  return '#3b5bdb';
}
